class PredictiveSearch extends HTMLElement {
	constructor() {
		super();
		this.cachedResults = {};
		this.input = this.querySelector('input[type="search"]');
		this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
		this.isOpen = false;

		this.setupEventListeners();
	}

	setupEventListeners() {
		const form = this.querySelector('form.search');

		if (!form) {
			return;
		}

		form.addEventListener('submit', this.onFormSubmit.bind(this));
		this.select = this.querySelector('select');

		this.input.addEventListener('input', debounce((event) => {
			this.onChange(event);
		}, 300).bind(this));

		if (this.select) {
			this.select.addEventListener('input', debounce((event) => {
				this.onChange(event);
			}, 300).bind(this));
		}

		this.input.addEventListener('focus', this.onFocus.bind(this));
		document.addEventListener('click', (event) => {
			const searchResults = document.querySelector('.predictive-search.predictive-search-header.color-background-1')
			if(searchResults) {
				if(!event.target.closest('.search.header-search-global-form.color-background-1')) {
					this.close();
					this.togglePageUIElements(false);
				}
			}
		});
		this.addEventListener('keyup', this.onKeyup.bind(this));
		this.addEventListener('keydown', this.onKeydown.bind(this));
		this.addEventListener('resize', this.handleResize.bind(this));
	}

	getQuery() {
		return this.input.value.trim();
	}

	getSearchFilterType() {
		if (!this.select) {
			return '';
		}

		return this.select.dataset.type;
	}

	getSearchFilterTypeQuery() {
		if (!this.select) {
			return '';
		}

		return this.select.value.trim();
	}

	onChange() {
		const searchTerm = this.getQuery();

		if (!searchTerm.length) {
			this.close(true);
			document.body.style.overflow = '';
			document.body.style.touchAction = '';
			const feefo = document.getElementById('feefo-service-review-floating-widgetId');
			const chat = document.getElementById('shopify-chat');
			const needsclick = document.querySelector('.needsclick.kl-teaser-ScySkJ');
			if (feefo) feefo.style.display = 'block';
			if (chat) chat.style.display = 'block';
			if (needsclick) needsclick.style.display = 'block';
			this.lockModalScroll();

			return;
		}

		this.getSearchResults(searchTerm);
	}

	onFormSubmit(event) {
		if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
			event.preventDefault();
		}
	}

	togglePageUIElements(hide = true) {
		const displayValue = hide ? 'none' : 'block';
		document.body.style.overflow = hide ? 'hidden' : '';
		document.body.style.touchAction = hide ? 'none' : '';
		document.body.style.webkitOverflowScrolling = hide ? 'touch' : '';
		document.body.style.overscrollBehavior = hide ? 'contain' : '';  
		this.lockModalScroll();

		const feefo = document.getElementById('feefo-service-review-floating-widgetId');
		const chat = document.getElementById('shopify-chat');
		const needsclick = document.querySelector('.needsclick.kl-teaser-ScySkJ');
		if (feefo) feefo.style.display = displayValue;
		if (chat) chat.style.display = displayValue;
		if (needsclick) needsclick.style.display = displayValue;
	}
	
	onFocus() {
		const searchTerm = this.getQuery();

		if (!searchTerm.length) {
			return;
		}

		if (this.getAttribute('results') === 'true') {
			this.open();
			this.togglePageUIElements(true);
		} else {
			this.getSearchResults(searchTerm);
		}
	}

	onKeyup(event) {
		if (!this.getQuery().length) {
			this.close(true);
			this.togglePageUIElements(false);
		}

		event.preventDefault();

		switch (event.code) {
			case 'ArrowUp':
				this.switchOption('up')
				break;
			case 'ArrowDown':
				this.switchOption('down');
				break;
			case 'Enter':
				this.selectOption();
				break;
		}
	}

	onKeydown(event) {
		// Prevent the cursor from moving in the input when using the up and down arrow keys
		if (
			event.code === 'ArrowUp' ||
			event.code === 'ArrowDown'
		) {
			event.preventDefault();
		}
	}

	switchOption(direction) {
		if (!this.getAttribute('open')) {
			return;
		}

		const moveUp = direction === 'up';
		const selectedElement = this.querySelector('[aria-selected="true"]');

		// Filter out hidden elements (duplicated page and article resources) thanks
		// to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
		const allVisibleElements = Array.from(
			this.querySelectorAll("li, button.predictive-search-item-button")
		).filter((element) => element.offsetParent !== null);
		let activeElementIndex = 0;

		if (moveUp && !selectedElement) return;

		let selectedElementIndex = -1;
		let i = 0;

		while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
			if (allVisibleElements[i] === selectedElement) {
				selectedElementIndex = i;
			}
			i++;
		}

		this.statusElement.textContent = "";

		if (!moveUp && selectedElement) {
			activeElementIndex =
				selectedElementIndex === allVisibleElements.length - 1
				? 0
				: selectedElementIndex + 1;
		} else if (moveUp) {
			activeElementIndex =
				selectedElementIndex === 0
				? allVisibleElements.length - 1
				: selectedElementIndex - 1;
		}

		if (activeElementIndex === selectedElementIndex) return;

		const activeElement = allVisibleElements[activeElementIndex];

		activeElement.setAttribute('aria-selected', true);
		if (selectedElement) selectedElement.setAttribute('aria-selected', false);

		this.input.setAttribute('aria-activedescendant', activeElement.id);
	}

	selectOption() {
		const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

		if (selectedProduct) {
			selectedProduct.click();
		}
	}

	getSearchResults(searchTerm) {
		const filterType      = this.getSearchFilterType();
		const filterTypeQuery = this.getSearchFilterTypeQuery();
		const queryKey = searchTerm.replaceAll(" ", "-").toLowerCase() + '-' + filterType + '-' + filterTypeQuery.replaceAll(" ", "-").toLowerCase();

		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			return;
		}

		const limit = this.dataset.limit;
		const limitScope = this.dataset.limitScope;
		const resultTypes = this.dataset.types;
		const showUnavailable = this.dataset.unavailable;
		const query = filterTypeQuery ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`) : encodeURIComponent(searchTerm);

		fetch(`${routes.predictive_search_url}?q=${query}&resources[type]=${resultTypes}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&options[unavailable_products]=${showUnavailable}&section_id=predictive-search`)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					this.togglePageUIElements(false);
					throw error;
				}

				return response.text();
			})
			.then((text) => {
				const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
				this.cachedResults[queryKey] = resultsMarkup;
				this.renderSearchResults(resultsMarkup);
			})
			.catch((error) => {
				this.close();
				this.togglePageUIElements(false);
				throw error;
			});
	}

	setLiveRegionLoadingState() {
		this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
		this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

		this.setLiveRegionText(this.loadingText);
		this.setAttribute('loading', true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.setAttribute('aria-hidden', 'false');
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute('aria-hidden', 'true');
		}, 1000);
	}

	getOpenResourceHeadings() {
		return Array.from(document.querySelectorAll('.sidebar-resource-group .resource-heading.active'))
			.filter(heading => !heading.classList.contains('resource-heading--product-grid'))
			.map(heading => heading.textContent.trim());
	}

	restoreOpenResourceHeadings(openHeadings) {
		document.querySelectorAll('.sidebar-resource-group .resource-heading').forEach(heading => {
			const headingText = heading.textContent.trim();
			if (
				openHeadings.includes(headingText) &&
				!heading.classList.contains('resource-heading--product-grid')
			) {
				const content = heading.nextElementSibling;
				if (content && content.classList.contains('resource-content')) {
					content.style.maxHeight = content.scrollHeight + 'px';
					heading.classList.add('active');
				}
			}
		});
	}

	handleResize() {
		if (window.innerWidth > 990) {
			document.querySelectorAll('.resource-heading.active').forEach((heading) => {
				heading.classList.remove('active');
				const content = heading.nextElementSibling;
				if (content && content.classList.contains('resource-content')) {
					content.style.maxHeight = null;
				}
				const icon = heading.querySelector('svg');
				if (icon) {
					icon.classList.remove('rotated');
				}
			});
		}
	}

	attachSidebarGroupListeners() {
		document.querySelectorAll('.sidebar-resource-group .resource-heading').forEach(heading => {
			if (heading.classList.contains('resource-heading--product-grid')) return;
			heading.addEventListener('click', () => {
				if (window.innerWidth > 990) return;
				const content = heading.nextElementSibling;
				if (content && content.classList.contains('resource-content')) {
					const isOpen = content.style.maxHeight && content.style.maxHeight !== '0px';

					// Toggle content visibility
					if (isOpen) {
						content.style.maxHeight = '0';
						heading.classList.remove('active');
					} else {
						content.style.maxHeight = content.scrollHeight + 'px';
						heading.classList.add('active');
					}
				}
			});
		});
	}

	lockModalScroll() {
		const modal = this.predictiveSearchResults;
		if (!modal) return;
	
		modal.addEventListener('touchstart', function (e) {
			modal._startY = e.touches[0].clientY;
		}, { passive: false });
	
		modal.addEventListener('touchmove', function (e) {
			const scrollTop = modal.scrollTop;
			const scrollHeight = modal.scrollHeight;
			const offsetHeight = modal.offsetHeight;
			const currentY = e.touches[0].clientY;
			const isScrollingUp = currentY > modal._startY;
			const isScrollingDown = currentY < modal._startY;
	
			const atTop = scrollTop === 0;
			const atBottom = scrollTop + offsetHeight >= scrollHeight;
	
			// Prevent body scroll when modal reaches edges
			if ((atTop && isScrollingUp) || (atBottom && isScrollingDown)) {
				e.preventDefault();
			}
		}, { passive: false });
	}	

	renderSearchResults(resultsMarkup) {

		const openHeadings = this.getOpenResourceHeadings(); 

		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute('results', true);
		this.togglePageUIElements(true);

		this.setLiveRegionResults();
		this.open();
		this.attachSidebarGroupListeners();
		this.restoreOpenResourceHeadings(openHeadings);
	}

	setLiveRegionResults() {
		this.removeAttribute('loading');
		this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
	}

	getResultsMaxHeight() {
		this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
		return this.resultsMaxHeight;
	}

	open() {
		this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
		this.setAttribute('open', true);
		this.input.setAttribute('aria-expanded', true);

		if (!this.isOpen) {
			const event = new CustomEvent('search:open');
			document.dispatchEvent(event);
		}

		this.isOpen = true;
	}

	close(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = '';
			this.removeAttribute('results');
		}

		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) {
			selected.setAttribute('aria-selected', false);
		}

		this.input.setAttribute('aria-activedescendant', '');
		this.removeAttribute('open');
		this.input.setAttribute('aria-expanded', false);
		this.resultsMaxHeight = false
		this.predictiveSearchResults.removeAttribute('style');

		if (this.isOpen) {
			const event = new CustomEvent('search:close');
			document.dispatchEvent(event);
		}

		this.isOpen = false;
	}
}

customElements.define('predictive-search', PredictiveSearch);
